import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { brand, principles, testimonials, stats } from "@/mocks/site";
import TipForm from "@/pages/about/components/TipForm";

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 黑暗爆料的内容原则与做法",
    description:
      "黑暗爆料是一个专注行业观察与消费警示的内容平台。这里介绍我们的品牌定位、内容原则、发展理念、读者口碑与线索征集方式，坚持可核验、可对照、不做未核实指控。",
    keywords: "黑暗爆料,关于我们,内容原则,行业观察,消费警示,线索征集,品牌理念",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `关于 ${brand.name}`,
        description:
          "黑暗爆料的品牌定位、内容原则、发展理念与线索征集方式。",
        url: "https://i2785.cn/about",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: brand.name,
        url: "https://i2785.cn/",
        email: brand.email,
        foundingDate: brand.since,
        description: brand.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://i2785.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "关于我们",
            item: "https://i2785.cn/about",
          },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="pt-16 md:pt-[72px]">
        <section className="bg-background-100 border-b border-background-200">
          <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-10 md:py-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight max-w-4xl">
              一个把「环节」讲清楚的
              <br className="hidden md:block" />
              行业观察与消费警示平台
            </h1>
            <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {brand.intro}
            </p>
            <dl className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6 max-w-3xl">
              {stats.map((item) => (
                <div key={item.label} className="border-l-2 border-primary-400 pl-4">
                  <dt className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {item.value}
                  </dt>
                  <dd className="mt-1 text-xs text-foreground-500">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="max-w-[1240px] mx-auto px-4 md:px-10 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden border border-background-200 aspect-[4/3] bg-secondary-900">
                <img
                  src="https://readdy.ai/api/search-image?query=dark%20moody%20still%20life%20of%20stacked%20plain%20manila%20folders%20and%20a%20single%20sealed%20envelope%20on%20an%20ink%20black%20desk%20lit%20by%20warm%20amber%20side%20light%20with%20a%20faint%20crimson%20accent%2C%20cinematic%20editorial%20photography%2C%20deep%20shadows%2C%20no%20people%2C%20no%20text&width=900&height=680&seq=about-dossier-stack-2026&orientation=landscape"
                  alt="暗色桌面上成叠的档案夹与一个密封信封，琥珀色侧光"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="flex items-center gap-2 text-xs font-medium tracking-[0.24em] uppercase text-primary-600 mb-4">
                <span className="w-6 h-px bg-primary-400" aria-hidden="true"></span>
                我们是谁
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
                从一封读者来信开始
              </h2>
              <div className="mt-5 space-y-4 text-sm md:text-base text-foreground-700 leading-[1.9]">
                <p>
                  {brand.since} 年，我们收到第一封读者来信：一位业主在装修后期才发现报价单外的增项已经吃掉了他大半预算。他没有被骗得很惨，只是一路都在「先做着，最后一起算」。
                </p>
                <p>
                  这封信让我们意识到一个问题——大多数让人吃亏的事，都不是因为有人足够坏，而是因为信息被分散在流程的各个角落，普通人很难把它们串起来。
                </p>
                <p>
                  于是{brand.name}变成了现在这样：不追热点、不博眼球，只做一件事——把一次交易、一份合同、一条流程，按真实发生的顺序还原出来，标出真正决定结果的那几个环节。
                </p>
              </div>
              <div className="mt-7 rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                <p className="text-sm text-foreground-700 leading-relaxed">
                  <strong className="text-foreground-950">网站定位：</strong>
                  面向普通读者与一线从业者的行业观察与消费警示平台，提供可对照、可复用的风险信息与提问方法。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="bg-background-100 border-y border-background-200 scroll-mt-20">
          <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-14 md:py-20">
            <p className="flex items-center gap-2 text-xs font-medium tracking-[0.24em] uppercase text-primary-600 mb-4">
              <span className="w-6 h-px bg-primary-400" aria-hidden="true"></span>
              内容原则
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl">
              有些线，我们不会跨过去
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              做调查类内容最难的不是写得多，而是知道什么不该写。以下四条是我们给自己划的边界。
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {principles.map((principle, index) => (
                <article
                  key={principle.title}
                  className={`flex gap-5 p-5 md:p-6 rounded-lg border ${
                    index === 0
                      ? "bg-background-50 border-primary-200"
                      : "bg-background-50 border-background-200"
                  }`}
                >
                  <span
                    className={`shrink-0 w-12 h-12 flex items-center justify-center rounded-md text-2xl ${
                      index % 2 === 0
                        ? "bg-primary-500 text-background-50"
                        : "bg-secondary-500 text-background-50"
                    }`}
                  >
                    <i className={principle.icon} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                      {principle.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{principle.desc}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  我们的发展理念
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  内容只做加法不做噱头。宁可一篇文章只讲清楚一个环节，也不写十段听起来刺激却无法验证的话。
                </p>
              </div>
              <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  我们怎么面对错误
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  每篇档案都标注最近修订时间。出现新信息或更正时，我们会在页面说明改动，而不是悄悄替换。
                </p>
              </div>
              <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                  我们不做什么
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  不收取任何费用，不做付费社群，也不提供内推或维权代理服务。任何以我们名义收费的行为都与平台无关。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-[1240px] mx-auto px-4 md:px-10 py-14 md:py-20">
          <p className="flex items-center gap-2 text-xs font-medium tracking-[0.24em] uppercase text-primary-600 mb-4">
            <span className="w-6 h-px bg-primary-400" aria-hidden="true"></span>
            读者口碑
          </p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight max-w-2xl">
            他们说，读完以后多问了一句
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex flex-col p-5 md:p-7 rounded-lg bg-background-100 border border-background-200"
              >
                <i className="ri-double-quotes-l text-3xl text-primary-400 mb-4" aria-hidden="true"></i>
                <blockquote className="text-sm md:text-base text-foreground-700 leading-relaxed flex-1">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-background-200 text-sm">
                  <span className="font-medium text-foreground-950">{item.name}</span>
                  <span className="text-foreground-500"> · {item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="submit" className="bg-foreground-950 scroll-mt-20">
          <div className="max-w-[1240px] mx-auto px-4 md:px-10 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-5">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                  提供爆料线索
                </h2>
                <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed">
                  你不需要准备好完整的证据链，只要把真实发生过的环节写下来就够了。剩下的比对与拆解交给我们。
                </p>
                <ul className="mt-8 space-y-4">
                  <li className="flex gap-3 text-sm text-background-200">
                    <i className="ri-checkbox-circle-line text-accent-400 text-lg shrink-0" aria-hidden="true"></i>
                    全程匿名，不公开你的联系方式
                  </li>
                  <li className="flex gap-3 text-sm text-background-200">
                    <i className="ri-checkbox-circle-line text-accent-400 text-lg shrink-0" aria-hidden="true"></i>
                    不转交任何第三方，也不用于交易
                  </li>
                  <li className="flex gap-3 text-sm text-background-200">
                    <i className="ri-checkbox-circle-line text-accent-400 text-lg shrink-0" aria-hidden="true"></i>
                    需要核实时，只通过你留下的邮箱联系
                  </li>
                </ul>
                <div className="mt-8 rounded-lg border border-background-50/15 bg-background-50/5 p-5">
                  <p className="text-xs text-background-300 mb-1">邮件联系</p>
                  <p className="font-heading text-lg text-background-50 break-all">{brand.email}</p>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-lg bg-background-50 p-5 md:p-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                    填写线索信息
                  </h3>
                  <TipForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}